import React from 'react'
import { getThemePalette } from '../Misc/style'
import { BaseFlex, CenterFlex, ColFlex, RowFlex } from './Misc'
import { AppStateContext } from '../App'

// Modern, sleek chat bubble styles
const chatBubbleStyles = `
.loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.loading-dots .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  opacity: 0;
  animation: loadingDot 1.4s infinite ease-in-out;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes loadingDot {
  0%, 80%, 100% { 
    opacity: 0;
    transform: scale(0.8);
  }
  40% { 
    opacity: 1;
    transform: scale(1);
  }
}

.chat-message {
  animation: slideUpFromBottom 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
  transform: translateY(30px);
}

@keyframes slideUpFromBottom {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.chat-bubble {
  position: relative;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 6px 20px rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.chat-bubble:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 12px 32px rgba(0, 0, 0, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.chat-bubble.received {
  background: linear-gradient(153deg, rgba(240, 147, 251, 0.66) 0%, rgba(102, 126, 234, 0.66) 36%, rgba(79, 172, 254, 0.66) 77%);
  color: #1a1a1a;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.1);
}

.chat-bubble.sent {
  background: linear-gradient(153deg, rgba(240, 147, 251, 0.66) 0%, rgba(102, 126, 234, 0.66) 36%, rgba(79, 172, 254, 0.66) 77%);
  color: #1a1a1a;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.1);
  font-weight: 800;
}

.chat-bubble.loading {
  background: linear-gradient(153deg, rgba(240, 147, 251, 0.66) 0%, rgba(102, 126, 234, 0.66) 36%, rgba(79, 172, 254, 0.63) 77%);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.chat-container {
  scrollbar-gutter: stable;
}

.chat-container::-webkit-scrollbar {
  width: 8px;
}

.chat-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.chat-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 4px;
}

.chat-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.6);
}

.message-content {
  line-height: 1.3;
  font-weight: 400;
  letter-spacing: 0.2px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  transition: all 0.2s ease;
}

.message-content:hover {
  transform: scale(1.02);
  letter-spacing: 0.4px;
}

// .chat-tail {
//   position: absolute;
//   width: 0;
//   height: 0;
//   z-index: -1;
// }

// .chat-tail.sent {
//   top: 12px;
//   right: -8px;
//   border-left: 12px solid #764ba2;
//   border-top: 8px solid transparent;
//   border-bottom: 8px solid transparent;
// }

// .chat-tail.received {
//   top: 12px;
//   left: -8px;
//   border-right: 12px solid #f5576c;
//   border-top: 8px solid transparent;
//   border-bottom: 8px solid transparent;
// }
`;

export type ContentText = {
    type: 'text',
    text: string,
}
export type ContentImage = {
    type: 'image',
    url: string,
}
export type ContentPdf = {
    type: 'pdf',
    url: string,
}
export type ContentChoice = {
    type: 'choice',
    text: string,
}
export type MessageContent =
    ContentText |
    ContentImage |
    ContentPdf |
    ContentChoice

export type ChatMessage = {
    direction: 'sent' | 'received',
    contents: MessageContent[],
    ttsText?: string
}
export type ChatHistory = ChatMessage[]

export const ChatHistoryBox: React.FC<{chatHistory: ChatHistory, onContentPress: (c: MessageContent) => void}> = props => {
    const appState = React.useContext(AppStateContext)
    const themePalette = getThemePalette(appState)
    const chatContainerRef = React.useRef<HTMLDivElement>(null)
    const [isAtBottom, setIsAtBottom] = React.useState(true)

    // Check if user is at bottom of chat
    const checkIfAtBottom = () => {
        if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
            setIsAtBottom(atBottom);
        }
    };

    // Auto-scroll to bottom only if user was already at bottom
    React.useEffect(() => {
        if (chatContainerRef.current && isAtBottom) {
            setTimeout(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                }
            }, 50); // Small delay to allow for animation
        }
    }, [props.chatHistory, isAtBottom])

    // Add scroll event listener
    React.useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkIfAtBottom);
            return () => container.removeEventListener('scroll', checkIfAtBottom);
        }
    }, []);

    const baseMargin = (mult: number) => 'calc(min(2vw, 2vh) * ' + mult + ')'
    const halfVertBalloonMargin = baseMargin(0.3)
    const bigBalloonMargin = baseMargin(5)
    const smallBalloonMargin = baseMargin(2.5)
    const balloonBorderRadius = baseMargin(0.8)
    
    const renderMessage = (m: ChatMessage, index: number) => {
        // Check if this is a loading message
        const isLoadingMessage = 
            m.direction === 'received' && 
            m.contents.length === 1 && 
            m.contents[0].type === 'text' && 
            m.contents[0].text === '...';
            
        const directionStyle: React.CSSProperties = m.direction === 'received' ?
            {
                textAlign: 'left',
                marginLeft: smallBalloonMargin,
                marginRight: bigBalloonMargin,
                alignItems: 'flex-start',
            }
        :
            {
                textAlign: 'right',
                marginLeft: bigBalloonMargin,
                marginRight: smallBalloonMargin,
                alignItems: 'flex-end',
            }
            
        const bubbleClass = isLoadingMessage ? 'chat-bubble loading' : `chat-bubble ${m.direction}`;
        
        // Return special loading indicator for loading messages
        if (isLoadingMessage) {
            return (
                <div key={index} className="chat-message" style={{
                    ...directionStyle, 
                    marginTop: halfVertBalloonMargin, 
                    marginBottom: halfVertBalloonMargin, 
                    display: 'flex', 
                    flexDirection: 'column',
                    animationDelay: `${index * 0.1}s`
                }}>
                    <div className={bubbleClass} style={{
                        paddingTop: halfVertBalloonMargin,
                        paddingBottom: halfVertBalloonMargin,
                        paddingLeft: halfVertBalloonMargin,
                        paddingRight: halfVertBalloonMargin,
                        borderRadius: balloonBorderRadius,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '90%',
                    }}>
                        <div className="loading-dots">
                            <span className="dot"></span>
                            <span className="dot"></span>
                            <span className="dot"></span>
                        </div>
                    </div>
                    <div className={`chat-tail ${m.direction}`} />
                </div>
            );
        }
        
        // Return regular message rendering
        return <div key={index} className="chat-message" style={{
            ...directionStyle, 
            marginTop: halfVertBalloonMargin, 
            marginBottom: halfVertBalloonMargin, 
            display: 'flex', 
            flexDirection: 'column',
            animationDelay: `${index * 0.1}s`
        }}>
            <div className={bubbleClass} style={{
                paddingTop: halfVertBalloonMargin,
                paddingBottom: halfVertBalloonMargin,
                paddingLeft: balloonBorderRadius,
                paddingRight: balloonBorderRadius,
                borderRadius: balloonBorderRadius,
                display: 'flex',
                flexDirection: 'column',
                gap: baseMargin(0.5),
                maxWidth: '90%',
                textAlign: 'justify',
                wordWrap: 'normal',
            }}>
                {
                    m.contents.map((content, index) => {
                        return (
                            content.type === 'text' ?
                                <div key={index} className="message-content" style={{
                                    whiteSpace: 'pre-line',
                                    fontSize: baseMargin(0.85),
                                    fontWeight: 400,
                                    lineHeight: 1.3,
                                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                                    letterSpacing: '0.2px',
                                    textRendering: 'optimizeLegibility',
                                    WebkitFontSmoothing: 'antialiased',
                                    MozOsxFontSmoothing: 'grayscale',
                                }}>{content.text}</div> :
                            content.type === 'image' ?
                                <BaseFlex key={index} onPress={() => props.onContentPress(content)} style={{
                                    borderRadius: baseMargin(1.5),
                                    overflow: 'hidden',
                                    transition: 'transform 0.2s ease',
                                    cursor: 'pointer',
                                }}>
                                    <img key={index} alt="" src={content.url} style={{
                                        width: baseMargin(15),
                                        borderRadius: baseMargin(1.5),
                                    }} />
                                </BaseFlex> :
                            content.type === 'pdf' ?
                                <BaseFlex key={index} onPress={() => props.onContentPress(content)} 
                                    hoverBgColor="rgba(255, 255, 255, 0.1)" 
                                    style={{
                                        borderRadius: baseMargin(1.5),
                                        padding: baseMargin(0.8),
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                    }}>
                                    <img alt="PDF document" src='images/pdf-icon.png' style={{
                                        width: baseMargin(10),
                                        filter: 'brightness(0) invert(1)',
                                    }} />
                                </BaseFlex> :
                            content.type === 'choice' ?
                                <RowFlex key={index} onPress={() => props.onContentPress(content)} style={{
                                    alignItems: 'center', 
                                    borderRadius: baseMargin(1.5),
                                    padding: baseMargin(0.8),
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                }} hoverBgColor="rgba(255, 255, 255, 0.1)">
                                    <BaseFlex style={{
                                        width: baseMargin(1.5), 
                                        height: baseMargin(1.5), 
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                                        borderRadius: baseMargin(0.75), 
                                        marginRight: baseMargin(1)
                                    }} />
                                    <span className="message-content" style={{
                                        fontSize: baseMargin(0.85),
                                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                                        fontWeight: 500,
                                        letterSpacing: '0.1px'
                                    }}>{content.text}</span>
                                </RowFlex> :
                            null
                        )
                    })
                }
            </div>
            <div className={`chat-tail ${m.direction}`} />
        </div>
    }
    
    return (
        <>
            <style>{chatBubbleStyles}</style>
            <div style={{
                display: 'flex', 
                position: 'absolute', 
                inset: 0, 
                flexDirection: 'column', 
                background: themePalette.chatBg,
                backgroundAttachment: 'fixed',
            }}>
                <div ref={chatContainerRef} style={{
                    display: 'flex', 
                    boxSizing: 'border-box', 
                    flexDirection: 'column', 
                    height: '100%',
                    width: '100%', 
                    overflow: 'auto',
                    padding: halfVertBalloonMargin,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255, 255, 255, 0.4) rgba(255, 255, 255, 0.1)',
                    scrollBehavior: 'smooth',
                }} className="chat-container">
                    {/* Spacer to push messages to bottom when few messages */}
                    <div style={{ flex: '1 1 auto', minHeight: '0' }} />
                    {/* Messages container */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '0 0 auto',
                    }}>
                        {
                            // Messages display in normal order but stacked from bottom
                            props.chatHistory.map((m, i) => renderMessage(m, i))
                        }
                    </div>
                </div>
            </div>
        </>
    )
}

export const ChatHistoryBoxWithContentOverlay: React.FC<{chatHistory: ChatHistory}> = props => {
    const [overlayContent, setOverlayContent] = React.useState<null | MessageContent>(null)

    const onContentPress = (content: MessageContent) => {
        if (content.type === 'pdf' || content.type === 'image') {
            setOverlayContent(content)

            window.history.pushState({}, 'overlay', null)
            window.addEventListener('popstate', function popOverlay() {
                setOverlayContent(null)
                window.removeEventListener('popstate', popOverlay);
            })
        }
    }

    return (
        <div style={{display: 'flex', position: 'absolute', inset: 0}}>
            <ChatHistoryBox onContentPress={onContentPress} {...props} />
            {overlayContent !== null && <ColFlex onPress={() => setOverlayContent(null)} style={{position: 'absolute', inset: 0, backgroundColor: 'black'}}>
                <RowFlex style={{color: 'white', alignSelf: 'flex-end', marginRight: '5%'}}>X</RowFlex>
                {overlayContent.type === 'image' ? <img src={overlayContent.url} alt="Overlay content" style={{height: '100%', width: '100%', objectFit: 'contain'}} /> :
                null}
            </ColFlex>}
        </div>
    )
}